'use client';

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { tv } from 'tailwind-variants';
import type { Item, Field, LinkField } from '@sitecore-content-sdk/nextjs';
import { Content } from '.generated/Content/WillsForm.model';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { LinkFieldValue } from '@sitecore-content-sdk/nextjs';
import WillCapacityNoEmailBody from '../EmailTemplates/WillCapacityNoBody';
import WillCapacityYesEmailBody from '../EmailTemplates/WillCapacityYesBody';
import { WILLS_FORM_CONSTANTS, FORM_CONSTANTS } from 'helpers/Constants/SiteConfig';
import { apiRequest } from 'lib/utils/apiWrapper';
import { encryptData } from 'lib/utils/encryption';
import { clearFormState } from '../../utils/storage';
import { clearCompletedWillsSteps } from '../../utils/completedStepsStorage';
import { sendGTMEvent } from '@next/third-parties/google';
import { toolStartFinishGTMEvent } from 'lib/utils/gtm-utils';
import type { FormValues, FormInstance, StepConfig } from '../../types';

export type willsFormProps = Content.WillsForm.OnlineWills_Component;

export type WillsFormSubmissionParams = NonNullable<Content.WillsForm.OnlineWills_Component['params']>;

export type SubmissionSuccessProps = {
  values: FormValues;
  completedForms: Record<string, FormInstance[]>;
  allSteps: StepConfig[];
  emailInfo: {
    willCapacityConcernYesOption?: Item;
    willCapacityConcernNoOption?: Item;
    acknowledgement?: Item;
    lpaAttachment?: Item;
    successTitleWithoutCapacity?: Field<string>;
    successDescriptionWithoutCapacity?: Field<string>;
    successTitleWithCapacity?: Field<string>;
    successDescriptionWithCapacity?: Field<string>;
    backToWillsWritingServiceHubLink?: LinkField;
  };
  localStorageKey: string;
  params?: WillsFormSubmissionParams;
};

export type emailDataProps = Record<string, unknown> | null;

const ADMIN_EMAIL_KEYS = ['adminEmailBody', 'adminFrom', 'adminSubject', 'adminTo'] as const;
const USER_EMAIL_KEYS = ['userFrom', 'userSubject', 'userEmailBody', 'userEmail'] as const;

function getFieldStringValue(value: unknown): string {
  if (value === null || value === undefined) return '';

  // Common case: Sitecore Field-like { value: string }
  if (typeof value === 'object') {
    const maybe = value as { value?: unknown };
    const v = maybe.value;
    return typeof v === 'string' ? v : '';
  }

  return '';
}

export const SubmissionSuccess: React.FC<SubmissionSuccessProps> = ({
  values,
  completedForms,
  allSteps,
  emailInfo,
  localStorageKey,
  params,
}) => {
  const [emailData, setEmailData] = useState<emailDataProps>(null);
  const [isReadyToSubmit, setIsReadyToSubmit] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitError, setIsSubmitError] = useState<boolean>(false);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState<boolean>(false);

  const hasSubmittedRef = useRef(false);

  const willsFormContainerRef = useRef<HTMLDivElement | null>(null);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const {
    willCapacityConcernYesOption,
    willCapacityConcernNoOption,
    acknowledgement,
    lpaAttachment,
    successTitleWithoutCapacity,
    successDescriptionWithoutCapacity,
    successTitleWithCapacity,
    successDescriptionWithCapacity,
  } = emailInfo;

  const [thankyouTitle, setThankyouTitle] = useState<string>(
    values.isCapacityConcern === 'No'
      ? successTitleWithoutCapacity?.value || ''
      : successTitleWithCapacity?.value || ''
  );
  const [thankyouSuccessMessage, setThankyouSuccessMessage] = useState<string>(
    values.isCapacityConcern === 'No'
      ? successDescriptionWithoutCapacity?.value || ''
      : successDescriptionWithCapacity?.value || ''
  );

  const {
    container,
    headerContainer,
    title,
    richcontentText,
    footerContainer,
    footerLink,
    loaderContainer,
    loaderSpinner,
    errorTitle,
    errorText,
  } = TAILWIND_VARIANTS();

  const emailbodyContent = useMemo(
    () =>
      renderToStaticMarkup(
        values.isCapacityConcern === 'No' ? (
          <WillCapacityNoEmailBody
            values={values}
            completedForms={completedForms}
            allSteps={allSteps}
          />
        ) : (
          <WillCapacityYesEmailBody values={values} allSteps={allSteps} />
        )
      ),
    [values, completedForms, allSteps]
  );

  const replaceEmailBodyToken = useCallback(
    (text: string | undefined, emailbodyContent: string): string => {
      if (!text || typeof window === 'undefined') {
        return text || '';
      }
      return text.replace(/\{\{willsEmailBodyContent\}\}/g, emailbodyContent);
    },
    []
  );

  const replaceFormSubmissionDate = useCallback((text: string | undefined): string => {
    const currentDate = new Date().toLocaleDateString('en-GB', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false, // Use 24-hour format
    });
    if (!text || typeof window === 'undefined') {
      return text || '';
    }
    return text.replace(/\{\{formSubmitDate\}\}/g, currentDate);
  }, []);

  const emailRelatedFieldsInfo = useMemo(() => {
    let emailRelatedFields: Record<string, unknown> = {};

    const isCapacityNo = values.isCapacityConcern === 'No';
    const shouldAttachPdf =
      isCapacityNo &&
      (values.estateBeneficiaryLPAPreference === 'Yes' ||
        values.estateBeneficiaryLPAContactPreference === 'Yes');

    if (isCapacityNo) {
      const noFields = willCapacityConcernNoOption?.fields;
      const noAdminEmailBody = noFields?.adminEmailBody as Field<string> | undefined;
      const noAdminSubject = noFields?.adminSubject as Field<string> | undefined;
      const noUserEmailBody = noFields?.userEmailBody as Field<string> | undefined;

      const adminEmailBodyValue = replaceEmailBodyToken(noAdminEmailBody?.value || '', emailbodyContent);

      const baseAdmin = {
        adminEmailBody: {
          ...(((noAdminEmailBody as unknown) ?? {}) as Record<string, unknown>),
          value: adminEmailBodyValue,
        },
        adminFrom: noFields?.adminFrom,
        adminSubject: params?.partnerName
          ? {
              ...(((noAdminSubject as unknown) ?? {}) as Record<string, unknown>),
              value: `${params?.partnerName} ${noAdminSubject?.value ?? ''}`,
            }
          : {
              ...(((noAdminSubject as unknown) ?? {}) as Record<string, unknown>),
              value: noAdminSubject?.value,
            },
        adminTo: noFields?.adminTo,
        userEmail: { value: values.email },
      };

      if (shouldAttachPdf) {
        const ackFields = acknowledgement?.fields;
        const ackUserEmailBody = ackFields?.userEmailBody as Field<string> | undefined;
        emailRelatedFields = {
          ...baseAdmin,
          userEmailBody: {
            ...(((ackUserEmailBody as unknown) ?? {}) as Record<string, unknown>),
            value: ackUserEmailBody?.value,
          },
          userFrom: ackFields?.userFrom,
          userSubject: ackFields?.userSubject,
          isPDFAttachment: true,
          attachmentURL: (lpaAttachment as { value?: { href?: string } } | undefined)?.value,
        };
      } else {
        emailRelatedFields = {
          ...baseAdmin,
          userEmailBody: {
            ...(((noUserEmailBody as unknown) ?? {}) as Record<string, unknown>),
            value: noUserEmailBody?.value,
          },
          userFrom: noFields?.userFrom,
          userSubject: noFields?.userSubject,
          isPDFAttachment: false,
        };
      }
    } else {
      const yesFields = willCapacityConcernYesOption?.fields;
      const yesAdminEmailBody = yesFields?.adminEmailBody as Field<string> | undefined;
      const yesAdminSubject = yesFields?.adminSubject as Field<string> | undefined;
      const yesUserEmailBody = yesFields?.userEmailBody as Field<string> | undefined;

      const updatedAdminEmailBodyValue = replaceEmailBodyToken(
        yesAdminEmailBody?.value || '',
        emailbodyContent
      );
      emailRelatedFields = {
        adminEmailBody: {
          ...(((yesAdminEmailBody as unknown) ?? {}) as Record<string, unknown>),
          value: replaceFormSubmissionDate(
            (updatedAdminEmailBodyValue as string | undefined) || ''
          ),
        },
        adminFrom: yesFields?.adminFrom,
        adminSubject: {
          ...(((yesAdminSubject as unknown) ?? {}) as Record<string, unknown>),
          value: yesAdminSubject?.value,
        },
        adminTo: yesFields?.adminTo,
        userEmailBody: {
          ...(((yesUserEmailBody as unknown) ?? {}) as Record<string, unknown>),
          value: yesUserEmailBody?.value,
        },
        userFrom: yesFields?.userFrom,
        userSubject: yesFields?.userSubject,
        userEmail: { value: values.capacityConcernEmail },
        isPDFAttachment: false,
      };
    }

    return emailRelatedFields;
  }, [
    values.isCapacityConcern,
    values.estateBeneficiaryLPAPreference,
    values.estateBeneficiaryLPAContactPreference,
    values.email,
    values.capacityConcernEmail,
    emailbodyContent,
    replaceEmailBodyToken,
    willCapacityConcernYesOption,
    willCapacityConcernNoOption,
    acknowledgement,
    lpaAttachment,
    replaceFormSubmissionDate,
    params,
  ]);

  useEffect(() => {
    willsFormContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    setEmailData(emailRelatedFieldsInfo);
  }, [emailRelatedFieldsInfo]);

  const backToWillsWritingServiceHubLink = emailInfo.backToWillsWritingServiceHubLink as
    | LinkFieldValue
    | undefined;

  useEffect(() => {
    if (values.isCapacityConcern === 'No') {
      setThankyouTitle(emailInfo.successTitleWithoutCapacity?.value || '');
      setThankyouSuccessMessage(emailInfo.successDescriptionWithoutCapacity?.value || '');
    } else {
      setThankyouTitle(emailInfo.successTitleWithCapacity?.value || '');
      setThankyouSuccessMessage(emailInfo.successDescriptionWithCapacity?.value || '');
    }
  }, [values, emailInfo]);

  const sendEmailData = useCallback(async (): Promise<boolean> => {
    if (!emailData) return false;

    setIsSubmitError(false);
    setIsSubmitSuccess(false);

    if (!executeRecaptcha) {
      console.error('reCAPTCHA is not available (missing site key or provider)');
      setIsSubmitError(true);
      setIsLoading(false);
      return false;
    }

    let recaptchaToken: string;
    try {
      recaptchaToken = await executeRecaptcha(WILLS_FORM_CONSTANTS.RECAPTCHA_ACTION);
    } catch (error) {
      console.error('Failed to generate reCAPTCHA token:', error);
      setIsSubmitError(true);
      setIsLoading(false);
      return false;
    }

    if (!recaptchaToken) {
      setIsSubmitError(true);
      setIsLoading(false);
      return false;
    }

    const data = {
      ...emailData,
      recaptcha_token: recaptchaToken,
    };

    const gtmEventSubmit = toolStartFinishGTMEvent({
      toolName: 'online_will',
      event: 'tool_submit',
    });
    sendGTMEvent(gtmEventSubmit);

    try {
      const response = await apiRequest(WILLS_FORM_CONSTANTS.ENDPOINT_URL, {
        method: 'POST',
        data: await encryptData(data, process.env.ENCRYPTION_KEY || ''),
        headers: {
          sourceSystem: FORM_CONSTANTS.SOURCE_SYSTEM,
        },
      });

      if (response.success) {
        setIsSubmitSuccess(true);
        setIsLoading(false);
        clearFormState(localStorageKey);
        clearCompletedWillsSteps();
        return true;
      }
      setIsSubmitError(true);
      setIsLoading(false);
      return false;
    } catch (error) {
      console.error('Error sending email data:', error);
      setIsSubmitError(true);
      setIsLoading(false);
      return false;
    }
  }, [emailData, executeRecaptcha, localStorageKey]);

  useEffect(() => {
    if (!emailData || hasSubmittedRef.current || isReadyToSubmit) return;

    const requiredKeys =
      values.isCapacityConcern === 'No'
        ? [...ADMIN_EMAIL_KEYS, ...USER_EMAIL_KEYS]
        : ADMIN_EMAIL_KEYS;
    const isPayloadReady = requiredKeys.every((k) => getFieldStringValue(emailData[k]) !== '');

    if (isPayloadReady) {
      hasSubmittedRef.current = true;
      setIsReadyToSubmit(true);

      void (async () => {
        const ok = await sendEmailData();
        setIsLoading(false);

        if (!ok) {
          setThankyouTitle('Error');
          setThankyouSuccessMessage('Something went wrong please try again');
        }
      })();
    }
  }, [emailData, sendEmailData, isReadyToSubmit, values.isCapacityConcern]);

  return (
    <div ref={willsFormContainerRef} className="scroll-mt-64">
      {isLoading && (
        <div className={container()}>
          <div className={loaderContainer()}>
            <div className={loaderSpinner()} aria-hidden="true" />
            <span className="sr-only">Sending your request</span>
          </div>
        </div>
      )}
      {isSubmitError && (
        <div className={container()}>
          <div className={headerContainer()}>
            <PlainTextWrapper
              field={{ value: 'Something went wrong' }}
              tag="h2"
              className={errorTitle()}
            />
            <RichTextWrapper
              field={{
                value:
                  '<p class="!mb-0">We’re sorry, there’s been an issue and your form couldn’t be submitted.</p><p>Please try again or call us directly on <a href="tel:03701500100">0370 1500 100</a> and we will be happy to support.</p>',
              }}
              className={errorText()}
            />
          </div>

          <div className={footerContainer()}>
            {backToWillsWritingServiceHubLink ? (
              <LinkWrapper
                field={backToWillsWritingServiceHubLink}
                ctaComponentClass="default"
                ctaVariant="secondaryInverse"
                suppressNewTabIcon={true}
                className={footerLink()}
              />
            ) : null}
          </div>
        </div>
      )}
      {isSubmitSuccess && (
        <div className={container()}>
          <div className={headerContainer()}>
            <div className="">
              <PlainTextWrapper field={{ value: thankyouTitle }} tag="h2" className={title()} />
            </div>
            <div className="">
              <RichTextWrapper
                field={{
                  value: thankyouSuccessMessage,
                }}
                className={richcontentText()}
              />
            </div>
          </div>

          <div className={footerContainer()}>
            {backToWillsWritingServiceHubLink ? (
              <LinkWrapper
                field={backToWillsWritingServiceHubLink}
                ctaComponentClass="default"
                ctaVariant="secondaryInverse"
                suppressNewTabIcon={true}
                className={footerLink()}
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    container: [
      'w-full',
      'bg-color-surface-white',
      'general-border-radius-card',
      'shadow-[0_0_8px_0_var(--color-black-alpha-10)]',
    ],
    headerContainer: ['w-full', 'p-padding-more', 'flex', 'flex-col', 'gap-spacing-spacing-24'],
    title: [
      'font-typography-font-family-header',
      'text-typography-font-size-h2',
      'text-color-core-color-1-Navy',
      'font-normal',
      'leading-typography-line-height-h2',
    ],
    richcontentText: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-large',
      'leading-typography-line-height-body-large',
      'font-normal',
      'text-color-text-default',
    ],
    completedFormsContainer: ['w-full', 'h-auto', 'p-8', 'overflow-x-auto'],
    footerContainer: [
      'w-full',
      'p-padding-more',
      'border-t-component-card-item-border-width border-component-Profile-Block-divider',
    ],
    footerLink: [
      'w-fit',
      'flex',
      'justify-start',
      'max-w-fit',
      'py-spacing-spacing-16',
      'px-spacing-spacing-24',
    ],
    loaderContainer: [
      'w-full',
      'p-padding-more',
      'flex',
      'items-center',
      'justify-center',
      'min-h-[240px]',
    ],
    loaderSpinner: [
      'h-10',
      'w-10',
      'rounded-full',
      'border-4',
      'border-color-core-color-1-Navy',
      'border-t-transparent',
      'animate-spin',
    ],
    errorTitle: [
      'font-typography-font-family-header',
      'text-typography-font-size-h2',
      'text-color-core-color-1-Navy',
      'font-normal',
      'leading-typography-line-height-h2',
    ],
    errorText: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-large',
      'leading-typography-line-height-body-large',
      'font-normal',
      'text-color-text-default',
    ],
  },
});
